import { useState, type ChangeEvent } from "react";
import { apiFetch } from "../api/client";
import type { QuestionImageRef } from "../api/types";

/**
 * Admin-only widget that lists a question's images, lets the admin upload
 * new ones (PNG/JPEG/GIF/WebP), and remove existing ones. Mounted inside
 * the EditQuestionForm on TestEditorPage so it's only visible once the
 * question has an id.
 */

const ALLOWED_MIME = ["image/png", "image/jpeg", "image/gif", "image/webp"];
const MAX_BYTES = 4 * 1024 * 1024;

function readAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(r.error ?? new Error("FileReader failed"));
    r.readAsDataURL(file);
  });
}

export function QuestionImageManager({
  questionId,
  initialImages,
  onChange,
}: {
  questionId: string;
  initialImages: QuestionImageRef[];
  onChange: (images: QuestionImageRef[]) => void;
}) {
  const [images, setImages] = useState<QuestionImageRef[]>(initialImages);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [altText, setAltText] = useState("");

  function update(next: QuestionImageRef[]) {
    setImages(next);
    onChange(next);
  }

  async function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    setError(null);
    if (!ALLOWED_MIME.includes(file.type)) {
      setError(`Unsupported file type (${file.type}). Allowed: PNG, JPEG, GIF, WebP.`);
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(`Image is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 4 MB.`);
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await readAsDataURL(file);
      const [, base64] = dataUrl.split(",");
      const res = await apiFetch<{ image: QuestionImageRef }>(
        `/admin/questions/${questionId}/images`,
        {
          method: "POST",
          body: JSON.stringify({
            data: base64,
            mime_type: file.type,
            alt_text: altText.trim() || undefined,
          }),
        },
      );
      update([...images, res.image]);
      setAltText("");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function deleteImage(imageId: string) {
    setError(null);
    setBusy(true);
    try {
      await apiFetch<void>(`/admin/question-images/${imageId}`, {
        method: "DELETE",
      });
      update(images.filter((i) => i.id !== imageId));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="image-manager">
      <p>
        <strong>Images</strong>
      </p>
      {error && (
        <p className="error-banner" role="alert">
          {error}
        </p>
      )}
      {images.length > 0 && (
        <div className="image-manager-grid">
          {images
            .slice()
            .sort((a, b) => a.position - b.position)
            .map((img) => (
              <div key={img.id} className="image-manager-item">
                <img
                  src={`/api/admin/question-images/${img.id}`}
                  alt={img.alt_text ?? ""}
                  className="image-manager-thumb"
                />
                {img.alt_text && (
                  <small className="image-manager-alt">
                    {img.alt_text}
                  </small>
                )}
                <button
                  type="button"
                  onClick={() => void deleteImage(img.id)}
                  disabled={busy}
                >
                  Remove
                </button>
              </div>
            ))}
        </div>
      )}
      <label className="field-block">
        Alt text (optional)
        <input
          className="field-wide"
          value={altText}
          onChange={(e) => setAltText(e.target.value)}
          placeholder="Describe the image for accessibility"
        />
      </label>
      <label className="field-block">
        Add image (PNG / JPEG / GIF / WebP, max 4 MB)
        <input
          type="file"
          accept={ALLOWED_MIME.join(",")}
          onChange={(e) => void handleFile(e)}
          disabled={busy || images.length >= 5}
        />
      </label>
      {images.length >= 5 && (
        <p>
          <small>Maximum of 5 images per question reached.</small>
        </p>
      )}
    </div>
  );
}
